'use client';

import { MotionConfig, motion, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

const CELLS = Array.from({ length: 100 }, (_, i) => i);

const ERAS = [
  {
    key: 'before',
    failures: 12,
    label: 'Old stack',
    caption: 'failed in every 100',
  },
  {
    key: 'after',
    failures: 1,
    label: 'With the engine',
    caption: 'fails in every 100',
  },
] as const;

/**
 * The signature visual: one square per generation, a hundred per era, both
 * eras visible at once so the comparison needs no interaction. On scroll-in
 * the failed squares ink up with a small stagger; reduced motion renders the
 * finished state immediately.
 */
export function HundredGrid({ className }: { className?: string }) {
  const [inked, setInked] = useState(false);
  const [mounted, setMounted] = useState(false);
  const prefersReduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);

  // useReducedMotion() is null on the server and the real boolean on the
  // client's first render; gating on mounted keeps hydration byte-identical.
  useEffect(() => {
    setMounted(true);
  }, []);
  const reduceMotion = mounted && !!prefersReduced;

  useEffect(() => {
    const el = ref.current;
    if (!el || reduceMotion) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          setInked(true);
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reduceMotion]);

  const show = inked || !!reduceMotion;

  return (
    <MotionConfig reducedMotion="user">
      <div
        ref={ref}
        className={cn(
          'grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8',
          className
        )}
      >
        {ERAS.map((era, eraIndex) => (
          <figure key={era.key} className="m-0 flex flex-col gap-3">
            <div
              className="grid max-w-72 grid-cols-10 gap-1"
              role="img"
              aria-label={`${era.label}: ${era.failures} of every 100 generations failed.`}
            >
              {CELLS.map((i) => {
                const failed = i < era.failures;
                return (
                  <motion.span
                    key={i}
                    aria-hidden
                    initial={false}
                    animate={{
                      opacity: failed && show ? 1 : failed ? 0.14 : 0.14,
                      scale: failed && show ? 1 : 0.82,
                    }}
                    transition={{
                      duration: 0.45,
                      delay: reduceMotion ? 0 : eraIndex * 0.5 + i * 0.012,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className={cn(
                      'aspect-square w-full',
                      failed ? 'bg-foreground' : 'bg-muted-foreground'
                    )}
                  />
                );
              })}
            </div>

            <figcaption className="flex items-baseline gap-2.5">
              <b className="text-3xl leading-none font-semibold tabular-nums">
                {era.failures}
              </b>
              <span className="min-w-0 text-sm text-muted-foreground">
                {era.caption}
                <span className="block font-mono text-[10px] tracking-wider uppercase">
                  {era.label}
                </span>
              </span>
            </figcaption>
          </figure>
        ))}
      </div>
    </MotionConfig>
  );
}
